import type { VehicleTripTrackStatus } from '@/api/vehiclesApi';

/*
 Display helpers for the Trips tab. All wire times are ISO-8601 UTC and are formatted
 explicitly in Europe/London — the fleet's operational timezone — never the browser's
 local zone.
*/

const LONDON_DATE = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
});

const LONDON_TIME = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    hour: '2-digit',
    minute: '2-digit',
});

const LONDON_DATE_TIME = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
});

export function formatLondonDate(iso: string): string {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? 'Unknown' : LONDON_DATE.format(date);
}

export function formatLondonTime(iso: string): string {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? '--:--' : LONDON_TIME.format(date);
}

/** Full instant for panel fields ("Sun 3 Aug, 14:07"); null when absent/malformed. */
export function formatLondonDateTime(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? null : LONDON_DATE_TIME.format(date);
}

/** Today's Europe/London calendar date as yyyy-MM-dd (the API's day-range format). */
export function londonToday(): string {
    // en-CA formats as yyyy-MM-dd
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(new Date());
}

/** A London calendar date `days` before the given yyyy-MM-dd date. */
export function addDays(isoDate: string, days: number): string {
    const date = new Date(`${isoDate}T12:00:00Z`); // midday dodges any date-line ambiguity
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
}

export function formatDistance(distanceM: number | null | undefined): string {
    if (distanceM == null) return 'Unknown distance';
    return distanceM < 1000 ? `${String(distanceM)} m` : `${(distanceM / 1000).toFixed(1)} km`;
}

export function formatDuration(durationS: number | null | undefined): string {
    if (durationS == null) return 'Unknown duration';
    const minutes = Math.round(durationS / 60);
    if (minutes < 60) return `${String(minutes)} min`;
    return `${String(Math.floor(minutes / 60))} h ${String(minutes % 60)} min`;
}

/** Portal affordance per track state: only route_available offers a route; the rest explain themselves. */
export const TRACK_STATUS_LABELS: Record<VehicleTripTrackStatus, string> = {
    pending: 'Route loading',
    route_available: 'Route available',
    summary_only: 'Locations only',
    failed_retryable: 'Route loading',
    unavailable: 'No route',
};

export function formatTrackStatusLabel(status: VehicleTripTrackStatus): string {
    return TRACK_STATUS_LABELS[status];
}
